import React, { useRef, useCallback, useEffect, forwardRef } from 'react'
import classNames from 'classnames'
import { wrapperGallery } from '@/components/Gallery'
import Btn from './Btn'
import Preview from './Preview'
import styles from './Single.less'
import { isVideo } from './utils'

import { defaultFailurePic, defaultFailureVideo } from './helper'

const Single = forwardRef(
    (
        {
            id,
            children,
            value,
            onChange,
            thumbnail,
            autoPreview,
            disabled,
            defaultImg,
            cropRecSizes,
            className,
            disMaterialLib,
            cloudDiskFolderId,
            imageEditable,
            imageLabel,
            form,
            singleValidate,
            ...restProps
        },
        ref
    ) => {
        const $this = useRef({
            value: null
        })
        const $uploader = useRef(null)

        $this.current.value = value

        const sizeStyle = { width: `${thumbnail.width}px`, height: `${thumbnail.height}px` }

        const btnProps = {
            value,
            onChange,
            thumbnail,
            autoPreview,
            disMaterialLib,
            cloudDiskFolderId,
            ...restProps
        }

        const handleSelect = useCallback(() => {
            $uploader.current && $uploader.current.select()
        }, [])

        const handleDelete = () => {
            onChange(null)
        }

        // 图片加载失败时替换为默认图片 status置为 failurePic
        const handleFailurePic = target => {
            if (isVideo(target)) {
                onChange({ ...target, status: 'failurePic', url: defaultFailureVideo })
            } else {
                onChange({ ...target, status: 'failurePic', url: defaultImg })
            }
        }

        // 图片裁剪成功后替换图片
        const handleCropPic = target => {
            onChange({ ...target, status: 'done', url: target?.url })
        }

        // 图片编辑成功后替换图片
        const handleEditPic = target => {
            onChange({ ...target, status: 'done', url: target?.url })
        }

        useEffect(() => {
            return () => {
                const { value } = $this.current

                if (Array.isArray(value)) {
                    value.forEach(v => v && v.uploadTask && v.uploadTask.abort())
                } else {
                    value && value.uploadTask && value.uploadTask.abort()
                }
            }
        }, [])

        return (
            <div
                id={id}
                ref={ref}
                className={classNames(styles.container, className, {
                    [styles.isDisabled]: disabled
                })}
                style={sizeStyle}
            >
                {value ? (
                    <Preview
                        {...restProps}
                        form={form}
                        style={sizeStyle}
                        dataSource={value}
                        cropRecSizes={cropRecSizes}
                        thumbnail={thumbnail}
                        showProgress={autoPreview}
                        disabled={disabled}
                        imageEditable={imageEditable}
                        imageLabel={imageLabel}
                        singleValidate={singleValidate}
                        onSelect={handleSelect}
                        onDelete={handleDelete}
                        onFailure={handleFailurePic}
                        onCrop={handleCropPic}
                        onEdit={handleEditPic}
                    />
                ) : (
                    <Btn ref={$uploader} form={form} style={sizeStyle} thumbnail={thumbnail} {...btnProps} />
                )}

                {children}
            </div>
        )
    }
)

Single.defaultProps = {
    multiple: false,
    autoPreview: true,
    enableToast: true,
    disMaterialLib: false, // 展示素材库
    imageEditable: false,
    cloudDiskFolderId: null, // 已经关联的素材库
    thumbnail: {
        // 缩略图配置项
        width: 84,
        height: 84,
        quality: 70,
        allowMagnify: true,
        crop: true,
        type: 'image/jpeg'
    },
    defaultImg: defaultFailurePic,
    shareSignature: false // 是否获取OSS永久文件夹签名信息
}

export default wrapperGallery(Single)
